import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Card, CardContent, IconButton, Modal, Tab, Tabs, Tooltip, Badge } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ServiceRequestLogIcon from "@mui/icons-material/Article";
import SubjectIcon from "@mui/icons-material/Subject";
import moment from "moment";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import DataTable from "examples/Tables/DataTable";
import ServiceRequestDataTable from "examples/Tables/ServiceRequestDataTable";
import CollapsibleTable from "examples/Tables/ExpandTable";
import TabletTelemetryLogs from "./TabletTelemetryLogs";
import EventsTable from "../table-view/eventsTable";
import JobsTable from "../table-view/jobsTable";
import AlertRulesForm from "../alertrule/new";
import ServiceRequestsForm from "../service-request/new";
import ServiceRequestStatusLogForm from "../service-request/log";
import MultiSeviceRequestStatusEditForm from "../service-request/multiStatusEdit";
import CrudService from "services/cruds-service";
import breakpoints from "assets/theme/base/breakpoints";
import { DynamicDataTable, StyledBadge } from "../holykel-device/misc";

const modalCardSx = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "94%", md: "80%" },
  maxHeight: "80vh",
  overflowY: "auto",
  p: 3,
};

function getAddressItemId(device, deviceData) {
  return (
    deviceData?.address_item_id ??
    device?.attributes?.address_item_id ??
    device?.attributes?.EntityAddressItem?.address_item_id ??
    device?.attributes?.entity_address_item?.address_item_id
  );
}

export default function TabletWorkspace({ id, device, deviceData, deviceValue }) {
  const navigate = useNavigate();
  const savedTab = Number.parseInt(localStorage.getItem("serviceRequestTab") ?? "0", 10);

  const [selectedTab, setSelectedTab] = useState(Number.isNaN(savedTab) ? 0 : savedTab);
  const [tabsOrientation, setTabsOrientation] = useState("horizontal");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const countRef = useRef(0);

  const [openViewModal, setOpenViewModal] = useState(false);
  const [serviceRequestLogModal, setServiceRequestLogModal] = useState(false);
  const [openServiceModal, setServiceModal] = useState(false);
  const [multiStatusEditModal, setMultiStatusEditModal] = useState(false);

  const [alert, setAlert] = useState();
  const [alertRule, setAlertRule] = useState();
  const [alertTableData, setAlertTableData] = useState([]);
  const [alertRules, setAlertRules] = useState([]);
  const [alertRuleTableData, setAlertRuleTableData] = useState([]);
  const [serviceRequests, setServiceRequests] = useState([]);
  const [serviceRequest, setServiceRequest] = useState([]);
  const [serviceRequestTableData, setServiceRequestTableData] = useState([]);

  const [serviceRequestEditFlag, setServiceRequestEditFlag] = useState(false);
  const [serviceStatusUpdate, setServiceStatusUpdate] = useState("");
  const [dataTableUpdate, setDataTableUpdate] = useState("");
  const [serviceRequestId, setServiceRequestId] = useState("");
  const [logServiceRequestId, setLogServiceRequestId] = useState("");
  const [selectedRows, setSelectedRows] = useState({});
  const [selectedRowsIdArray, setSelectedRowsIdArray] = useState([]);
  const [checkRowSelectedRowsFlag, setCheckSelectedRowsFlag] = useState(false);
  const [selectedRequestType, setSelectedRequestType] = useState("");

  useEffect(() => {
    function handleTabsOrientation() {
      setTabsOrientation(window.innerWidth < breakpoints.values.sm ? "vertical" : "horizontal");
    }

    window.addEventListener("resize", handleTabsOrientation);
    handleTabsOrientation();
    return () => window.removeEventListener("resize", handleTabsOrientation);
  }, []);

  useEffect(() => {
    if (selectedTab === 1) allAlerts(page, rowsPerPage);
    if (selectedTab === 3) alertRulesList();
    if (selectedTab === 4) serviceRequestList();
  }, [id, selectedTab, page, rowsPerPage, dataTableUpdate, serviceStatusUpdate]);

  useEffect(() => {
    const ids = Object.keys(selectedRows).filter((key) => selectedRows[key]);
    setSelectedRowsIdArray(ids);
    setCheckSelectedRowsFlag(ids.length > 0);
    if (!serviceRequestId && ids.length > 0) setServiceRequestId(ids[0]);
  }, [selectedRows]);

  async function allAlerts(activePage, activeRowsPerPage) {
    try {
      const resp = await CrudService.getDeviceAlerts(id, activePage, activeRowsPerPage);
      setAlertTableData(getAlertRows(resp.data ?? [], deviceValue?.level_percent ?? 0));
      countRef.current = resp?.pagination?.total ?? resp?.count ?? 0;
    } catch (error) {
      console.error("tablet allAlerts", error);
    }
  }

  async function alertRulesList() {
    try {
      const resp = await CrudService.getAlertRules({ id });
      setAlertRules(resp.data ?? []);
      setAlertRuleTableData(getAlertRuleRows(resp.data ?? []));
    } catch (error) {
      console.error("tablet alertRulesList", error);
    }
  }

  async function serviceRequestList(addressId = null) {
    const addressItemId = addressId ?? getAddressItemId(device, deviceData);
    if (!addressItemId) return;

    try {
      const resp = await CrudService.getServiceRequests(addressItemId);
      setServiceRequests(resp.data ?? []);
      setServiceRequestTableData(getServiceRequestRows(resp.data ?? []));
    } catch (error) {
      console.error("tablet serviceRequestList", error);
    }
  }

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    localStorage.setItem("serviceRequestTab", newValue);
  };

  const getEventRows = (info = []) =>
    info.map((row) => ({
      id: row?.event_id,
      alert_type: row?.AlertType?.type_name,
      parameter_name: row?.DeviceParameter?.parameter_name,
      percent_value: row?.percent_change,
      prev_value: row?.previous_value,
      current_value: row?.new_value,
      volume: row?.volume_change,
      date: row?.event_timestamp,
    }));

  const eventColumns = [
    { Header: "ID", accessor: "id", show: true },
    { Header: "Type", accessor: "alert_type", show: true },
    { Header: "Parameter Name", accessor: "parameter_name", show: true },
    { Header: "Current Value", accessor: "current_value", show: true },
    { Header: "Previous Value", accessor: "prev_value", show: true },
    { Header: "Percent Change", accessor: "percent_value", show: true },
    { Header: "Volume Change (Gallons)", accessor: "volume", show: true },
    { Header: "Date", accessor: "date", show: true, Cell: (info) => info.value ? moment(info.value).format("MMMM DD, YYYY h:mm:ss A") : "--" },
  ];

  const jobColumns = [
    { Header: "ID", accessor: "id", show: true, Cell: (info) => <a href={`/job-view/${info.value}`} target="_blank" rel="noopener noreferrer">{info.value}</a> },
    { Header: "Name", accessor: "job_session_name", show: true },
    { Header: "Current State", accessor: "current_state", show: true },
    { Header: "Volume Collected", accessor: "volume_collected_gallons", show: true },
    { Header: "Volume Discharged", accessor: "volume_discharged_gallons", show: true },
    { Header: "Residual Volume", accessor: "residual_volume_gallons", show: true },
    { Header: "Total Duration", accessor: "total_duration_seconds", show: true },
    { Header: "Started At", accessor: "started_at", show: true },
    { Header: "Completed At", accessor: "completed_at", show: true },
    { Header: "State Changed At", accessor: "state_changed_at", show: true },
  ];

  function getAlertRows(info = [], percent = 0) {
    return info.map((row) => ({
      id: row.id,
      alert_type: row.attributes?.AlertType?.type_name,
      alert_type_category: row.attributes?.AlertType?.category,
      parameter_name: row.attributes?.DeviceParameter?.label ?? row.attributes?.DeviceParameter?.parameter_name ?? "",
      sub_type: row.attributes?.is_parent ? "Alert Created" : row.attributes?.is_healed ? "Alert Healed" : "Alert Resent",
      threshold_value: row.attributes?.threshold,
      exceed_value: row.attributes?.exceed_value,
      current_value: row.current_value ?? percent,
      created_at: row.attributes?.created_at ? moment(row.attributes.created_at).format("MMMM DD, YYYY h:mm:ss A") : "",
      updated_at: row.attributes?.updated_at ? moment(row.attributes.updated_at).format("MMMM DD, YYYY h:mm:ss A") : "",
      status_text: row.attributes?.StatusOption?.StatusType?.status_name || "",
      status: (
        <Badge
          color={row.attributes?.StatusOption?.BadgeDetail?.color || "primary"}
          variant={row.attributes?.StatusOption?.BadgeDetail?.variant}
          badgeContent={row.attributes?.StatusOption?.StatusType?.status_name}
          style={{ marginLeft: "30px" }}
        />
      ),
    }));
  }

  const alertColumns = [
    { Header: "ID", accessor: "id", show: true },
    { Header: "Status Badge", accessor: "status", show: true },
    { Header: "Status", accessor: "status_text", show: false },
    { Header: "Alert Type", accessor: "alert_type", show: true },
    { Header: "Parameter", accessor: "parameter_name", show: true },
    { Header: "Threshold Value", accessor: "threshold_value" },
    { Header: "Exceed Value", accessor: "exceed_value" },
    { Header: "Current Value", accessor: "current_value" },
    { Header: "Alert Category", accessor: "alert_type_category", show: true },
    { Header: "Sub Type", accessor: "sub_type", show: true },
    { Header: "Created At", accessor: "created_at", show: true },
    { Header: "Updated At", accessor: "updated_at", show: true },
    { Header: "actions", disableSortBy: true, accessor: "actions", show: true },
  ];

  function getAlertRuleRows(info = []) {
    return info.map((row) => {
      const attrs = row.attributes ?? row;
      return {
        id: row?.id ?? attrs?.rule_id,
        rule_type: attrs?.module_id === 12 ? "Alert" : attrs?.module_id === 13 ? "Service Request" : attrs?.module_id === 22 ? "Job State Alert" : "",
        alert_type: attrs?.AlertType?.type_name,
        alert_type_category: attrs?.AlertType?.category,
        alert_sub_type: attrs?.AlertSubType?.sub_type_name,
        event_type: attrs?.EventType?.name,
        message_template_name: attrs?.SendgridTemplate?.name ?? attrs?.MessageTemplate?.name ?? "",
        address: `${attrs?.Contact?.first_name ?? ""} ${attrs?.Contact?.last_name ?? ""} - ${attrs?.ContactAddress?.contact_address ?? ""}`,
        send_to_type: attrs?.SendToType?.send_to_type_name,
        frequency: attrs?.frequency ? `${attrs.frequency} min${attrs.frequency > 1 ? "(s)" : ""}` : "",
        state_from: attrs?.state_from || "",
        state_to: attrs?.state_to || "",
        status_text: attrs?.StatusOption?.StatusType?.status_name || "",
        status: (
          <Badge
            color={attrs?.StatusOption?.BadgeDetail?.color || "primary"}
            variant={attrs?.StatusOption?.BadgeDetail?.variant}
            badgeContent={attrs?.StatusOption?.StatusType?.status_name}
            style={{ marginLeft: "30px" }}
          />
        ),
      };
    });
  }

  async function clickAlertRuleEditHandler(ruleId) {
    const res = await CrudService.getAlertRule(ruleId);
    setAlertRule(res);
    setOpenViewModal(true);
  }

  async function clickAlertRuleDeleteHandler(e, ruleId) {
    if (!confirm("Are you sure you want to delete this alert rule?")) {
      e.nativeEvent.stopImmediatePropagation();
      return;
    }
    await CrudService.deleteAlertRule(ruleId);
    alertRulesList();
  }

  const alertRuleTable = {
    columns: [
      { Header: "ID", accessor: "id", show: true },
      { Header: "Status Badge", accessor: "status", show: true },
      { Header: "Rule Type", accessor: "rule_type", show: true },
      { Header: "Alert Type", accessor: "alert_type", show: true },
      { Header: "Alert Sub Type", accessor: "alert_sub_type", show: true },
      { Header: "Event Type", accessor: "event_type", show: true },
      { Header: "Message Template Name", accessor: "message_template_name", show: true },
      { Header: "Address", accessor: "address", show: true },
      { Header: "Send Type", accessor: "send_to_type", show: true },
      { Header: "Frequency", accessor: "frequency", show: true },
      { Header: "State From", accessor: "state_from", show: true },
      { Header: "State To", accessor: "state_to", show: true },
      {
        Header: "actions",
        accessor: "",
        show: true,
        Cell: (info) => (
          <MDBox display="flex">
            <Tooltip title="Edit Alert Rule">
              <IconButton onClick={() => clickAlertRuleEditHandler(info.cell.row.original.id)}><EditIcon /></IconButton>
            </Tooltip>
            <Tooltip title="Delete Alert Rule">
              <IconButton onClick={(e) => clickAlertRuleDeleteHandler(e, info.cell.row.original.id)}><DeleteIcon /></IconButton>
            </Tooltip>
          </MDBox>
        ),
      },
    ],
    rows: alertRuleTableData,
  };

  function getServiceRequestRows(info = []) {
    return info.map((row) => ({
      id: row.attributes?.service_request_id,
      user: row.attributes?.user?.name,
      entityAddressItem: row.attributes?.EntityAddressItem?.name,
      provider: row.attributes?.Provider?.provider_name,
      serviceItem: row.attributes?.ServiceItem?.item_name,
      request_date: row.attributes?.request_date,
      status: row.attributes?.StatusOption,
      status_text: row.attributes?.StatusOption?.StatusType?.status_name || "",
    }));
  }

  async function clickServiceRequestEditHandler(e, requestId) {
    e.stopPropagation();
    setServiceRequestEditFlag(true);
    const res = await CrudService.getServiceRequest(requestId);
    setServiceRequest(res.data.attributes);
    setServiceModal(true);
  }

  function clickServiceRequestLogHandler(e, requestId) {
    e.stopPropagation();
    setLogServiceRequestId(requestId);
    setServiceRequestLogModal(true);
  }

  function clickServiceRequestViewHandler(e, requestId) {
    e.stopPropagation();
    navigate(`/service-request-detail/${requestId}`);
  }

  async function clickServiceRequestDeleteHandler(e, requestId) {
    e.stopPropagation();
    if (!confirm("Are you sure you want to delete this service request?")) {
      e.nativeEvent.stopImmediatePropagation();
      return;
    }
    await CrudService.deleteServiceRequest(requestId);
    serviceRequestList();
  }

  const serviceRequestTable = {
    columns: [
      { Header: "ID", accessor: "id", show: true },
      {
        Header: "Status Badge",
        accessor: "",
        show: true,
        Cell: (info) => (
          <StyledBadge
            color={info.cell.row.original?.status?.BadgeDetail?.color || "primary"}
            variant={info.cell.row.original?.status?.BadgeDetail?.variant || "contained"}
            badgeContent={info.cell.row.original.status?.StatusType?.status_name}
            style={{ marginLeft: "30px" }}
          />
        ),
      },
      { Header: "Status", accessor: "status_text", show: true },
      { Header: "Entity Address Item", accessor: "entityAddressItem", show: true },
      { Header: "Provider", accessor: "provider", show: true },
      { Header: "Service Item", accessor: "serviceItem", show: true },
      { Header: "Requested Date", accessor: "request_date", show: true },
      { Header: "User", accessor: "user", show: true },
      {
        Header: "actions",
        accessor: "",
        show: true,
        Cell: (info) => (
          <MDBox display="flex">
            <Tooltip title="View Service Request"><IconButton onClick={(e) => clickServiceRequestViewHandler(e, info.cell.row.original.id)}><SubjectIcon /></IconButton></Tooltip>
            <Tooltip title="Show Status Change Log"><IconButton onClick={(e) => clickServiceRequestLogHandler(e, info.cell.row.original.id)}><ServiceRequestLogIcon /></IconButton></Tooltip>
            <Tooltip title="Edit Service Request"><IconButton onClick={(e) => clickServiceRequestEditHandler(e, info.cell.row.original.id)}><EditIcon /></IconButton></Tooltip>
            <Tooltip title="Delete Service Request"><IconButton onClick={(e) => clickServiceRequestDeleteHandler(e, info.cell.row.original.id)}><DeleteIcon /></IconButton></Tooltip>
          </MDBox>
        ),
      },
    ],
    rows: serviceRequestTableData,
  };

  function handleSelectedRows(requestId, flag) {
    const selectedRequest = serviceRequests.find((request) => request?.attributes?.service_request_id == requestId);
    setSelectedRequestType(selectedRequest?.attributes?.status_type_id);
    setSelectedRows((prev) => ({ ...prev, [requestId]: flag }));
  }

  function clickCancelSelection() {
    setSelectedRows({});
    setSelectedRowsIdArray([]);
    setCheckSelectedRowsFlag(false);
  }

  return (
    <>
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Tabs value={selectedTab} orientation={tabsOrientation} onChange={handleTabChange} sx={{ display: "flex", mb: 2 }}>
            <Tab label={<Box display="flex" alignItems="center" gap={1}><i className="fas fa-file-alt" />Logs</Box>} />
            <Tab label={<Box display="flex" alignItems="center" gap={1}><i className="fas fa-exclamation-triangle" />Alerts</Box>} />
            <Tab label={<Box display="flex" alignItems="center" gap={1}><i className="fa-solid fa-clock" />Events</Box>} />
            <Tab label={<Box display="flex" alignItems="center" gap={1}><i className="fas fa-code-branch" />Rules</Box>} />
            <Tab label={<Box display="flex" alignItems="center" gap={1}><i className="fas fa-tools" />Service Request</Box>} />
            <Tab label={<Box display="flex" alignItems="center" gap={1}><i className="fas fa-briefcase" />Jobs</Box>} />
          </Tabs>

          {selectedTab === 0 && <TabletTelemetryLogs key={id} deviceId={id} />}

          {selectedTab === 1 && (
            <CollapsibleTable
              width="100%"
              data={alertTableData}
              columns={alertColumns}
              clickViewHandler={async (alertId) => {
                const data = await CrudService.getAlert(alertId);
                setAlert(data);
                setOpenViewModal(true);
              }}
              clickDeleteHandler={async (e, alertId) => {
                if (confirm("Are you sure you want to delete this alert?")) {
                  await CrudService.deleteAlert(alertId);
                  allAlerts(page, rowsPerPage);
                } else {
                  e.nativeEvent.stopImmediatePropagation();
                }
              }}
              page={page}
              setPage={setPage}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
              count={countRef.current}
            />
          )}

          {selectedTab === 2 && <EventsTable columns={eventColumns} id={id} getRows={getEventRows} canSearch canDelete isSelection />}

          {selectedTab === 3 && (
            <>
              <MDBox display="flex" justifyContent="flex-end" mb={2}>
                <MDButton color="dark" size="small" onClick={() => { setAlertRule(); setOpenViewModal(true); }}>Add Rule</MDButton>
              </MDBox>
              <DataTable table={alertRuleTable} canSearch />
            </>
          )}

          {selectedTab === 4 && (
            <MDBox mt={2}>
              <MDBox mb={2} display="flex" justifyContent="space-between">
                <MDButton color="dark" size="small" onClick={() => { setServiceRequestEditFlag(false); setServiceRequest([]); setServiceModal(true); }}>
                  Add Service Request
                </MDButton>
                {checkRowSelectedRowsFlag && (
                  <MDBox display="flex" gap={1}>
                    <MDButton color="primary" size="small" onClick={() => setMultiStatusEditModal(true)}>Edit</MDButton>
                    <MDButton color="secondary" size="small" onClick={clickCancelSelection}>Cancel Selection</MDButton>
                  </MDBox>
                )}
              </MDBox>

              <ServiceRequestDataTable
                table={serviceRequestTable}
                canSearch
                onRowClick={setServiceRequestId}
                handleSelectedRows={handleSelectedRows}
                selectedRows={selectedRows}
                setDataTableUpdate={setDataTableUpdate}
              />
            </MDBox>
          )}

          {selectedTab === 5 && <JobsTable columns={jobColumns} id={id} getRows={(info) => info} canSearch canDelete={false} isSelection={false} />}
        </CardContent>
      </Card>

      <Modal open={openViewModal && selectedTab === 1} onClose={() => setOpenViewModal(false)}>
        <Card sx={modalCardSx}><CardContent><DynamicDataTable Alert={alert} /></CardContent></Card>
      </Modal>

      <Modal open={openViewModal && selectedTab === 3} onClose={() => setOpenViewModal(false)}>
        <Card sx={modalCardSx}>
          <AlertRulesForm alertRule={alertRule} setAlertRules={setAlertRules} deviceId={id} setOpenViewModal={setOpenViewModal} />
        </Card>
      </Modal>

      <Modal open={openServiceModal} onClose={() => setServiceModal(false)}>
        <Card sx={modalCardSx}>
          <ServiceRequestsForm
            deviceId={id}
            device={device?.attributes}
            serviceRequestEditFlag={serviceRequestEditFlag}
            serviceRequest={serviceRequest}
            setServiceModal={setServiceModal}
            setServiceRequests={setServiceRequests}
            setServiceStatusUpdate={setServiceStatusUpdate}
            showToast={(payload) => console.info("tablet service request", payload)}
          />
        </Card>
      </Modal>

      <Modal open={serviceRequestLogModal} onClose={() => setServiceRequestLogModal(false)}>
        <Card sx={modalCardSx}>
          <ServiceRequestStatusLogForm setServiceRequestLogModal={setServiceRequestLogModal} logServiceRequestId={logServiceRequestId} />
        </Card>
      </Modal>

      <Modal open={multiStatusEditModal} onClose={() => setMultiStatusEditModal(false)}>
        <Card sx={modalCardSx}>
          <MultiSeviceRequestStatusEditForm
            selectedRowsIdArray={selectedRowsIdArray}
            setMultiStatusEditModal={setMultiStatusEditModal}
            serviceRequestId={serviceRequestId || selectedRowsIdArray[0]}
            setDataTableUpdate={setDataTableUpdate}
            selectedRequestType={selectedRequestType}
            showToast={(payload) => console.info("tablet multi status", payload)}
            totalServiceRequestManagement={serviceRequests}
          />
        </Card>
      </Modal>
    </>
  );
}